import * as XLSX from 'xlsx';
import ConfirmModal from "../components/ConfirmModal";
import React, { useState, useEffect, useRef, useCallback } from "react";

const API = (process.env.REACT_APP_API_URL || "") + "";
const token = () => localStorage.getItem("token") || "";
const username = () => localStorage.getItem("username") || "Client";

async function apiFetch(path, opts = {}) {
  const r = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token(), ...(opts.headers || {}) },
  });
  if (!r.ok) {
    let _errMsg = "Something went wrong";
    try {
      const _errJson = await r.json();
      _errMsg = _errJson.detail || _errJson.message || JSON.stringify(_errJson);
      // "Meeting Error: " prefix hatao — clean message dikho
      _errMsg = _errMsg.replace(/^Meeting Error:\s*/i, "");
    } catch { _errMsg = await r.text().catch(() => "Request failed"); }
    throw new Error(_errMsg);
  }
  return r.json();
}

const USDT_RATE = 95;
const fmtBal = (bal, cur) => {
  const n = parseFloat(bal) || 0;
  if (cur === "USDT") return `$${(n / USDT_RATE).toFixed(4)} USDT`;
  return `₹${n.toFixed(2)}`;
};
const calcCost = (members, hours, pricePerMember, reactionPrice=0) => {
  // Special pricing — flat rate (any price that is NOT exactly 2.00)
  // Duration multiplier: 2h=1x, 3h=2x, 4h=3x, 5h+=4x
  const h = Math.round(hours);
  let multiplier = 1;
  if (h <= 2) multiplier = 1;
  else if (h === 3) multiplier = 2;
  else if (h === 4) multiplier = 3;
  else multiplier = 4;
  const unitPrice = (pricePerMember * multiplier) + (reactionPrice * multiplier);
  return members * unitPrice;
};

const FLOAT_EMOJIS = [
  { key: "clapping", emoji: "👏", label: "Clap" },
  { key: "thumbs_up", emoji: "👍", label: "Like" },
  { key: "joy", emoji: "😂", label: "Joy" },
  { key: "open_mouth", emoji: "😮", label: "Wow" },
  { key: "heart", emoji: "❤️", label: "Heart" },
];
const PART_REACTIONS = [
  { key: "yes", emoji: "✅", label: "Yes" },
  { key: "no", emoji: "❌", label: "No" },
  { key: "slow_down", emoji: "⏪", label: "Slow" },
  { key: "speed_up", emoji: "⏩", label: "Fast" },
  { key: "coffee", emoji: "☕", label: "Break" },
  { key: "raised_hand", emoji: "✋", label: "Hand" },
];
const NAME_OPTIONS = [
  { value: "indian", label: "🇮🇳 Indian Names" },
  { value: "foreign", label: "🌍 Foreign Names" },
  { value: "custom", label: "✏️ Custom Names" },
];
const MEETING_OPTIONS = [
  { value: "normal", label: "👥 Normal Participants" },
];

function fmtTime(ts) {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}
function fmtCountdown(ts) {
  const diff = Math.max(0, Math.floor(ts - Date.now() / 1000));
  const h = Math.floor(diff / 3600), m = Math.floor((diff % 3600) / 60), s = diff % 60;
  return `${h}h ${m}m ${s}s`;
}

const EMPTY_FORM = {
  name: "", zoom_url: "", passcode: "", total_bots: 10, stagger_secs: 3,
  leave_after_mins: 120, join_timeout_secs: 300, name_type: "indian", custom_names: "",
  meeting_type: "normal", floating_emoji: false, float_emoji_keys: [], float_interval: 0, float_count: 0,
  participant_reactions: false, part_reaction_keys: [], part_count: 0, part_interval: 0,
  part_remove_after: 30, part_start_after: 0, schedule_enabled: false, schedule_at: "",
  slow_join: false, custom_spread_mins: "",
};

function ConstellationBg() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf, stars = [];
    const N = 100;
    function resize() { canvas.width = window.innerWidth; canvas.height = window.innerHeight; }
    resize();
    window.addEventListener("resize", resize);
    const handleMouseMove = (e) => { mouseRef.current = { x: e.clientX, y: e.clientY }; };
    const handleTouch = (e) => { const t = e.touches[0]; if (t) mouseRef.current = { x: t.clientX, y: t.clientY }; };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("touchmove", handleTouch, { passive: true });
    for (let i = 0; i < N; i++) {
      const vx = (Math.random() - 0.5) * 0.1, vy = (Math.random() - 0.5) * 0.1;
      stars.push({ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight, ox: vx, oy: vy, vx, vy, r: Math.random() * 1.5 + 0.2, alpha: Math.random() * 0.6 + 0.2, pulse: Math.random() * Math.PI * 2, twinkle: Math.random() * 0.02 + 0.005 });
    }
    function draw() {
      const W = canvas.width, H = canvas.height;
      ctx.clearRect(0, 0, W, H);
      const bg = ctx.createRadialGradient(W * 0.25, H * 0.2, 0, W * 0.5, H * 0.5, W);
      bg.addColorStop(0, "#080d1f"); bg.addColorStop(0.4, "#050810"); bg.addColorStop(1, "#020408");
      ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
      [{ x: W*.1,y:H*.15,r:W*.3,c:[20,50,180,.04]},{x:W*.85,y:H*.55,r:W*.28,c:[100,20,200,.05]},{x:W*.45,y:H*.85,r:W*.22,c:[0,100,220,.035]},{x:W*.6,y:H*.1,r:W*.18,c:[60,20,160,.03]}].forEach(nb=>{
        const[r,g,b,a]=nb.c,grd=ctx.createRadialGradient(nb.x,nb.y,0,nb.x,nb.y,nb.r);
        grd.addColorStop(0,`rgba(${r},${g},${b},${a})`);grd.addColorStop(1,"transparent");
        ctx.fillStyle=grd;ctx.fillRect(0,0,W,H);
      });
      const mx=mouseRef.current.x,my=mouseRef.current.y,ATTRACT_RADIUS=200,ATTRACT_STRENGTH=0.013;
      stars.forEach(s=>{
        s.pulse+=s.twinkle;
        const dx=mx-s.x,dy=my-s.y,dist=Math.sqrt(dx*dx+dy*dy);
        if(dist<ATTRACT_RADIUS){const f=ATTRACT_STRENGTH*(1-dist/ATTRACT_RADIUS);s.vx+=dx*f;s.vy+=dy*f;}
        s.vx+=(s.ox-s.vx)*0.02;s.vy+=(s.oy-s.vy)*0.02;
        s.x+=s.vx;s.y+=s.vy;
        if(s.x<0)s.x=W;if(s.x>W)s.x=0;if(s.y<0)s.y=H;if(s.y>H)s.y=0;
        const a=s.alpha*(0.7+0.3*Math.sin(s.pulse));
        ctx.beginPath();ctx.arc(s.x,s.y,s.r,0,Math.PI*2);ctx.fillStyle=`rgba(180,210,255,${a})`;ctx.fill();
      });
      for(let i=0;i<stars.length;i++){for(let j=i+1;j<stars.length;j++){
        const dx=stars[i].x-stars[j].x,dy=stars[i].y-stars[j].y,d=Math.sqrt(dx*dx+dy*dy);
        if(d<90){ctx.beginPath();ctx.moveTo(stars[i].x,stars[i].y);ctx.lineTo(stars[j].x,stars[j].y);ctx.strokeStyle=`rgba(99,179,237,${0.09*(1-d/90)})`;ctx.lineWidth=0.5;ctx.stroke();}
      }}
      raf=requestAnimationFrame(draw);
    }
    draw();
    return()=>{cancelAnimationFrame(raf);window.removeEventListener("resize",resize);window.removeEventListener("mousemove",handleMouseMove);window.removeEventListener("touchmove",handleTouch);};
  },[]);
  return <canvas ref={canvasRef} style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none"}}/>;
}

function Dropdown({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  const sel = options.find(o => o.value === value) || options[0];
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onClick={() => setOpen(o => !o)} style={{ ...inp, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", userSelect: "none" }}>
        <span>{sel?.label}</span><span style={{ color: "#475569", fontSize: 10 }}>▼</span>
      </div>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "rgba(8,13,32,0.98)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9, zIndex: 300, overflow: "hidden", animation: "ddOpen .12s ease" }}>
          {options.map(o => (
            <div key={o.value} onClick={() => { onChange(o.value); setOpen(false); }} style={{ padding: "9px 13px", fontSize: 13, color: o.value === value ? "#63b3ed" : "#94a3b8", cursor: "pointer", background: o.value === value ? "rgba(99,179,237,0.08)" : "transparent", borderLeft: `2px solid ${o.value === value ? "#63b3ed" : "transparent"}`, transition: "all 0.1s" }}>{o.label}</div>
          ))}
        </div>
      )}
  </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <div onClick={() => onChange(!checked)} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer", padding: "5px 0" }}>
      <div style={{ width: 40, height: 22, borderRadius: 11, flexShrink: 0, position: "relative", background: checked ? "linear-gradient(135deg,#38bdf8,#818cf8)" : "rgba(255,255,255,0.09)", border: checked ? "none" : "1px solid rgba(255,255,255,0.1)", boxShadow: checked ? "0 0 14px rgba(56,189,248,0.4)" : "none", transition: "all 0.22s" }}>
        <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", left: checked ? 21 : 3, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.4)" }} />
      </div>
      <span style={{ fontSize: 13, color: checked ? "#ffffff" : "#f1f5f9", fontWeight: 700, lineHeight: 1, userSelect: "none" }}>{label}</span>
    </div>
  );
}

function Chips({ items, selected, onToggle }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
      {items.map(it => {
        const on = selected.includes(it.key);
        return <button key={it.key} onClick={() => onToggle(it.key)} style={{ border: `1px solid ${on ? "rgba(99,179,237,0.5)" : "rgba(255,255,255,0.1)"}`, background: on ? "rgba(99,179,237,0.13)" : "rgba(255,255,255,0.04)", color: on ? "#63b3ed" : "#cbd5e1", borderRadius: 20, padding: "4px 12px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", transition: "all 0.14s" }}>{it.emoji} {it.label}</button>;
      })}
    </div>
  );
}

function StatusDot({ status }) {
  const m = { running: ["#4ade80","Running"], joining: ["#38bdf8","Joining..."], pending: ["#fbbf24","Pending"], stopped: ["#f87171","Stopped"], scheduled: ["#818cf8","Scheduled"], failed: ["#f87171","Failed"], provisioning: ["#818cf8","Provisioning..."] };
  const [c, l] = m[status] || m.stopped;
  return <span style={{ display:"inline-flex",alignItems:"center",gap:5,fontSize:11,color:c,background:c+"18",border:`1px solid ${c}35`,padding:"3px 10px",borderRadius:20,fontWeight:700,letterSpacing:0.6,textTransform:"uppercase",whiteSpace:"nowrap" }}><span style={{width:6,height:6,borderRadius:"50%",background:c,boxShadow:status==="running"?`0 0 7px ${c}`:"none",flexShrink:0}}/>{l}</span>;
}

const glass = { background:"rgba(8,13,32,0.72)",backdropFilter:"blur(28px) saturate(1.5)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:16,boxShadow:"0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)" };
const inp = { width:"100%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.13)",color:"#e2e8f0",borderRadius:9,padding:"9px 13px",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box",transition:"border-color 0.18s, box-shadow 0.18s" };
const lbl = { fontSize:10,fontWeight:800,letterSpacing:1.3,textTransform:"uppercase",color:"#f1f5f9",display:"block",marginBottom:5 };

function InfoChip({ label, value, accent }) {
  return (
    <div style={{ background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.07)",borderRadius:8,padding:"6px 8px",textAlign:"center" }}>
      <div style={{ fontSize:9,color:"#475569",letterSpacing:0.8,textTransform:"uppercase",marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:12,fontWeight:700,color:accent||"#e2e8f0" }}>{value}</div>
    </div>
  );
}

function fmtReason(r) {
  if (!r) return null;
  const s = r.toLowerCase();
  if (s.includes("host_ended") || s.includes("host ended")) return "Host Ended";
  if (s.includes("meeting_full") || s.includes("at capacity")) return "Meeting Full";
  if (s.includes("does not exist") || s.includes("invalid meeting")) return "Invalid Link";
  if (s.includes("ended") || s.includes("over")) return "Meeting Ended";
  if (s.includes("stopped while waiting") || s.includes("waiting for meeting")) return "Meeting Offline";
  if (s.includes("bots aren") || s.includes("not allowed")) return "Bots Blocked";
  return r.slice(0, 28);
}

function CampCard({ c, onStop, onForce, stopReason }) {
  const joined=c.active||0,total=c.total_bots||1,pct=Math.min(100,Math.round((joined/total)*100));
  const isRun=c.status==="running"||c.status==="joining"||c.status==="provisioning";
  const hasReact=c.floating_emoji||c.participant_reactions;
  return (
    <div style={{ padding:"14px 16px",borderBottom:"1px solid rgba(255,255,255,.06)",background:isRun?"rgba(56,189,248,0.03)":"transparent" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
        <div>
          <div style={{ fontFamily:"'JetBrains Mono',monospace",fontSize:15,fontWeight:700,color:isRun?"#38bdf8":"#63b3ed",textShadow:isRun?"0 0 10px rgba(56,189,248,0.7)":"none" }}>#{c.name||"—"}</div>
          <div style={{ fontSize:10,color:"#4a6280",marginTop:2,fontFamily:"'JetBrains Mono',monospace",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{c.zoom_url?.slice(0,30)}</div>
        </div>
        <div style={{ textAlign:"right" }}>
          <StatusDot status={c.status}/>
          {!isRun && stopReason && <div style={{ fontSize:9,color:"#f87171",background:"rgba(248,113,113,.1)",border:"1px solid rgba(248,113,113,.25)",borderRadius:20,padding:"2px 7px",marginTop:4,display:"inline-block" }}>⚠ {fmtReason(stopReason)}</div>}
        </div>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:10 }}>
        <InfoChip label="Members" value={`${joined}/${total}`}/>
        <InfoChip label="Leave" value={c.leave_after_mins>0?c.leave_after_mins+"m":"∞"}/>
        <InfoChip label="React" value={hasReact?"✓ Yes":"✗ No"} accent={hasReact?"#4ade80":"#f87171"}/>
      </div>
      {isRun&&<div style={{ height:3,background:"rgba(255,255,255,.06)",borderRadius:3,marginBottom:10,overflow:"hidden" }}><div style={{ height:"100%",width:pct+"%",background:"linear-gradient(90deg,#38bdf8,#818cf8)",borderRadius:3 }}/></div>}
      <div style={{ fontSize:10,color:"#4a6280" }}>{c.name_type==="indian"?"🇮🇳 Hindi":c.name_type==="foreign"?"🌍 English":"✏️ Custom"} · {c.meeting_type==="webinar"?"🎙 Webinar":c.meeting_type==="profile_pic"?"👤 Profile":"👥 Normal"}</div>
    </div>
  );
}

function HistoryCard({ h, pricePerMember, reactionPricePerMember, currency }) {
  const hasReaction = !!(h.floating_emoji || h.participant_reactions);
  const baseP = pricePerMember + (hasReaction ? (reactionPricePerMember||0) : 0);
  const hours = (h.leave_after_mins||120)/60;
  const mul = Math.round(hours)<=2?1:Math.round(hours)===3?2:Math.round(hours)===4?3:4;
  const cost = (h.bots_sent||0) * baseP * mul;
  const hasReact=h.floating_emoji||h.participant_reactions;
  return (
    <div style={{ padding:"14px 16px",borderBottom:"1px solid rgba(255,255,255,.06)" }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
        <div>
          <div style={{ fontFamily:"'JetBrains Mono',monospace",fontSize:15,fontWeight:700,color:"#93c5fd" }}>#{h.name||"—"}</div>
          <div style={{ fontSize:10,color:"#4a6280",marginTop:2 }}>{h.zoom_url?.slice(0,30)}</div>
        </div>
        <StatusDot status={h.status}/>
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8 }}>
        <InfoChip label="Sent" value={`${h.bots_sent}/${h.total_bots||"—"}`}/>
        <InfoChip label="Leave" value={h.leave_after_mins>0?h.leave_after_mins+"m":"∞"}/>
        <InfoChip label="React" value={hasReact?"✓ Yes":"✗ No"} accent={hasReact?"#4ade80":"#f87171"}/>
      </div>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
        <div style={{ fontSize:10,color:"#4a6280" }}>{fmtTime(h.started_at)}</div>
        <div style={{ fontSize:12,fontWeight:700,color:"#f87171" }}>−{fmtBal(cost,currency)}</div>
      </div>
    </div>
  );
}

const downloadExcel = (data, filename, headers, rowFn) => {
  const rows = data.map((r, i) => {
    const vals = rowFn(r, i);
    const obj = {};
    headers.forEach((h, j) => { obj[h] = vals[j]; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, filename.replace('.csv', '.xlsx'));
};

function FundDownloadBtn({ data }) {
  const handleClick = () => {
    const headers = ["Date","Method","TXN ID","Amount","Status"];
    const rows = data.map(f => [
      new Date((f.created_at||0)*1000).toLocaleString(),
      f.method||"", f.txn_id||"", f.amount||0, f.status||""
    ]);
    downloadExcel(data, "fund_history.csv", headers, (_, i) => rows[i]);
  };
  return <button onClick={handleClick} style={{ marginLeft:"auto",background:"rgba(56,189,248,0.1)",border:"1px solid rgba(56,189,248,0.3)",color:"#38bdf8",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>⬇ Download</button>;
}

function DownloadBtn({ data, filename, price, reactionPricePerMember=0 }) {
  const handleClick = () => {
    const headers = ["Date","Task Name","Meeting URL / ID","Passcode","Members","Duration","Reactions","Cost","Status"];
    const rows = data.map(h => {
      const hrs = (h.leave_after_mins||120)/60;
      const hasReact = !!(h.floating_emoji || h.participant_reactions);
      const baseUnit = price>0 && price!==2 ? price : (hrs<=2?2:hrs<=3?4:hrs<=4?6:8);
      const reactionUnit = hasReact ? (reactionPricePerMember||0) * (baseUnit/2) : 0;
      const cost = (h.bots_sent||0) * (baseUnit + reactionUnit);
      const zurl = h.zoom_url||"";
      const idMatch = zurl.match(/zoom\.us\/j\/(\d+)/);
      const meetingCell = idMatch ? idMatch[1] : zurl;
      const passcodeCell = h.passcode||"";
      const hasReaction = !!(h.floating_emoji || h.participant_reactions);
      return [
        new Date((h.started_at||0)*1000).toLocaleString(),
        h.campaign_name||h.name||"",
        meetingCell,
        passcodeCell,
        h.bots_sent||0,
        (h.leave_after_mins||0)+"min",
        hasReaction ? "Yes" : "No",
        cost, h.status||""
      ];
    });
    downloadExcel(data, filename, headers, (_, i) => rows[i]);
  };
  return <button onClick={handleClick} style={{ background:"rgba(56,189,248,0.1)",border:"1px solid rgba(56,189,248,0.3)",color:"#38bdf8",borderRadius:8,padding:"5px 12px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>⬇ Download</button>;
}

export default function ClientDashboard() {
  const [campaigns, setCampaigns] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [history, setHistory]   = useState([]);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(()=>{ try{ const e=localStorage.getItem('cd_end'); if(!e) return 0; const r=Math.ceil((parseInt(e)-Date.now())/1000); return r>0?r:0; }catch{return 0;} });  // 60s cooldown after launch
  const [err, setErr]           = useState("");
  const [success, setSuccess]   = useState("");
  const [, setTick]             = useState(0);
  const [cPage, setCPage]       = useState(0);
  const [hPage, setHPage]       = useState(0);
  const [delConfirm, setDelConfirm] = useState(null);
  const [savedLists, setSavedLists] = useState(() => { try { return JSON.parse(localStorage.getItem("savedNameLists")||"{}"); } catch { return {}; } });
  const [listModal, setListModal]   = useState(false);
  const [listName, setListName]     = useState("");
  const [isMobile, setIsMobile]     = useState(window.innerWidth < 768);
  const [stopReasons, setStopReasons] = useState({});

  const [userProfile, setUserProfile] = useState({ balance: 0, display_currency: "INR", reactions_enabled: true, price_per_member: 2, reaction_price_per_member: 0 });
  const [fundModal, setFundModal]   = useState(false);
  const [activePage, setActivePage] = useState("dashboard");
  const [qrList, setQrList]         = useState([]);
  const [histPage, setHistPage]       = useState(0);
  const [payInstr, setPayInstr]       = useState("");
  const [fundHistory, setFundHistory] = useState([]);
  const [selQr, setSelQr]           = useState(null);
  const [txnId, setTxnId]           = useState("");
  const [fundAmt, setFundAmt]       = useState("");
  const [fundSubmitting, setFundSubmitting] = useState(false);
  const [fundErr, setFundErr]       = useState("");
  const [fundSuccess, setFundSuccess] = useState("");
  const [broadcast, setBroadcast] = useState(null);
  useEffect(() => {
    async function fetchBC() {
      try {
        const r = await fetch((process.env.REACT_APP_API_URL||"") + "/api/broadcast/active", { headers: { Authorization: "Bearer " + token() } });
        if (r.ok) { const d = await r.json(); setBroadcast(d.message || null); }
      } catch {}
    }
    fetchBC();
    const i = setInterval(fetchBC, 15000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => { const h = () => setIsMobile(window.innerWidth < 768); window.addEventListener("resize", h); return () => window.removeEventListener("resize", h); }, []);

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm(p => { const a = p[k]||[]; return { ...p, [k]: a.includes(v) ? a.filter(x=>x!==v) : [...a,v] }; });

  useEffect(() => {
    if (activePage === "addfund") {
      apiFetch("/api/payments/instructions").catch(()=>({text:""})).then(r => {
        setPayInstr((typeof r==="object"?r?.text:"")||"");
      });
      apiFetch("/api/payments/qr").catch(()=>[]).then(r => setQrList(r||[]));
    }
  }, [activePage]);

  const load = useCallback(async () => {
    try {
      const [c, s, h, me, qrs, instr, fh] = await Promise.all([
        apiFetch("/api/campaigns").catch(()=>null),
        apiFetch("/api/campaigns/scheduled/all").catch(()=>null),
        apiFetch("/api/campaigns/history/all").catch(()=>null),
        apiFetch("/api/me").catch(()=>null),
        apiFetch("/api/payments/qr").catch(()=>[]),
        apiFetch("/api/payments/instructions").catch(()=>({text:""})),
        apiFetch("/api/payments/history").catch(()=>[]),
      ]);
      // Sirf dynamic fields update karo — static fields preserve karo (no flicker)
      if (c?.length >= 0) setCampaigns(prev => {
        const prevMap = Object.fromEntries(prev.map(x => [x.id, x]));
        const serverMap = Object.fromEntries(c.map(x => [x.id, x]));
        const activeIds = new Set(c.map(x => x.id));
        const updated = prev.map(p => {
          const s = serverMap[p.id];
          if (!s) return p;
          const wasActive = ["running","joining","provisioning"].includes(p.status);
          const nowActive = ["running","joining","provisioning"].includes(s.status);
          if (wasActive && nowActive && p.status !== s.status) {
            return { ...p, status: s.status, active_bots: s.active_bots, bots_joined: s.bots_joined, active: s.active, total_bots: s.total_bots };
          }
          return { ...p, status: s.status, active_bots: s.active_bots, bots_joined: s.bots_joined, active: s.active };
        });
        const added = c.filter(x => !prevMap[x.id]);
        const filtered = updated.filter(p => {
          if (activeIds.has(p.id)) return true;
          return ["running","joining","provisioning","pending"].includes(p.status);
        });
        return [...filtered, ...added];
      });
      if (s?.length >= 0) setScheduled(s);
      if (h?.length >= 0) setHistory(h);
      if (qrs) setQrList(qrs||[]);
      if (fh) setFundHistory(fh||[]);
      setPayInstr((typeof instr==="object"?instr?.text:"")||"");
      if (me) setUserProfile({ balance: me.balance||0, display_currency: me.display_currency||"INR", reactions_enabled: me.reactions_enabled !== false, price_per_member: me.price_per_member||2, reaction_price_per_member: me.reaction_price_per_member||0 });
    } catch {}
  }, []);

  
  // Debounced load — 50 bots ek saath update kare to sirf 1 load hoga
  const loadDebouncedRef = useRef(null);
  const loadDebounced = useCallback(() => {
    if (loadDebouncedRef.current) clearTimeout(loadDebouncedRef.current);
    loadDebouncedRef.current = setTimeout(() => { load(); }, 1500);
  }, [load]);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    let sio=null,dead=false;
    function connect() {
      if(dead)return;
      try {
        const {io}=require("socket.io-client");
        const base=(process.env.REACT_APP_API_URL||window.location.origin);
        sio=io(base,{transports:["websocket","polling"],reconnectionDelay:1000});
        sio.on("connect",()=>{ sio.emit("join_dashboard",{}); });
        sio.on("connect",()=>{sio.emit("dashboard_connect");});
        sio.on("campaign_auto_stopped",(d)=>{ if(d?.campaign_id && d?.reason) setStopReasons(p=>({...p,[d.campaign_id]:d.reason})); loadDebounced(); });
        sio.on("bot_update",()=>{loadDebounced();});
        sio.on("campaign_update",()=>{loadDebounced();});
        sio.on("batch_complete",()=>{loadDebounced();});
        sio.on("disconnect",()=>{if(!dead)setTimeout(connect,5000);});
      }catch{}
    }
    connect();
    return()=>{dead=true;try{sio&&sio.disconnect();}catch{}};
  }, [load]);

  useEffect(() => { const t=setInterval(()=>setTick(n=>n+1),1000); return()=>clearInterval(t); }, []);

  async function submitCampaign() {
    setErr(""); setSuccess("");
    if (!form.name.trim()) return setErr("Task name required");
    if (!form.leave_after_mins||form.leave_after_mins<120) return setErr("Please select a Leave After duration");
    if (!form.total_bots||form.total_bots<10||form.total_bots%10!==0) return setErr("Total members must be a multiple of 10 (min 10)");
    if (!form.zoom_url.trim()) return setErr("Zoom URL required");
    if (form.name_type==="custom"&&!form.custom_names.trim()) return setErr("Enter custom names");
    if (form.schedule_enabled&&!form.schedule_at) return setErr("Set schedule time");
    if (form.schedule_enabled&&new Date(form.schedule_at).getTime()/1000 <= Date.now()/1000+60) return setErr("Schedule time must be at least 1 minute in the future (IST)");
    if (form.slow_join&&(!form.custom_spread_mins||+form.custom_spread_mins<=0)) return setErr("Enter a valid Slow Join spread duration (minutes)");
    if (!form.schedule_enabled && previewCost > 0 && bal < previewCost) return setErr("Insufficient balance. Please top up to launch this task.");
    const body = {
      name:form.name,zoom_url:form.zoom_url.replace(/\s+/g,""),passcode:form.passcode,
      total_bots:+form.total_bots,stagger_secs:+form.stagger_secs,leave_after_mins:parseInt(form.leave_after_mins,10)||0,
      custom_spread_mins:form.slow_join?+form.custom_spread_mins:null,
      join_timeout_secs:+form.join_timeout_secs,name_type:form.name_type,custom_names:form.custom_names,
      meeting_type:form.meeting_type,floating_emoji:form.floating_emoji,participant_reactions:form.participant_reactions,
      reaction_schedule:(()=>{let rs=[];if(form.floating_emoji&&form.float_emoji_keys?.length>0){for(let i=0;i<(+form.float_count||1);i++){form.float_emoji_keys.forEach(ek=>rs.push({emoji:ek,delay_secs:i*(+form.float_interval||0)}));}}return JSON.stringify(rs);})(),
      reaction_config:JSON.stringify({...(form.participant_reactions?{reactions:form.part_reaction_keys,count_per_wave:form.part_count,interval_secs:form.part_interval,remove_after:form.part_remove_after,start_after:form.part_start_after}:{}),float_emoji_keys:form.float_emoji_keys,float_interval:+form.float_interval,float_count:+form.float_count}),
      schedule_at:form.schedule_enabled?(new Date(form.schedule_at+":00+05:30").getTime()/1000):null,
    };
    setSubmitting(true);
    try {
      const res=await apiFetch("/api/campaigns",{method:"POST",body:JSON.stringify(body)});
      
      setSuccess(form.schedule_enabled?"✦ Task scheduled!":"✦ Task launched!");
      setForm(EMPTY_FORM);setCPage(0);setTimeout(()=>load(),2000);
      // 60s cooldown start karo
      setCooldown(60); try{localStorage.setItem('cd_end', Date.now()+60000);}catch{}
      const _cd = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) { clearInterval(_cd); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch(e){setErr(e.message);}finally{setSubmitting(false);}
  }

  const activeSched = scheduled.filter(s=>s.status==="waiting"||s.status==="cancelled");
  const waitingSchedCount = scheduled.filter(s=>s.status==="waiting").length;
  const PS = 4;
  const activeCampaigns = campaigns.filter(c=>["running","joining","provisioning"].includes(c.status));
  const pagedC = activeCampaigns.slice(cPage*PS,(cPage+1)*PS);
  const pagedH = history.slice(hPage*PS,(hPage+1)*PS);
  const uname = username();
  const cur = userProfile.display_currency;
  const price = userProfile.price_per_member;
  const reactionPrice = userProfile.reaction_price_per_member || 0;
  const formHasReaction = !!(form.floating_emoji || form.participant_reactions);
  const effectivePrice = price;

  const totalMembers = activeCampaigns.reduce((a,c)=>a+(c.total_bots||0),0);
  const totalSpend = history.reduce((a,h)=>{ const hrs=(h.leave_after_mins||120)/60; const mul=Math.round(hrs)<=2?1:Math.round(hrs)===3?2:Math.round(hrs)===4?3:4; const hasR=!!(h.floating_emoji||h.participant_reactions); const effP=price+(hasR?(reactionPrice||0):0); const c=(h.bots_sent||0)*effP*mul; return a+c; },0);
  const bal = userProfile.balance;
  const balColor = bal < 0 ? "#f87171" : bal < 50 ? "#fbbf24" : "#34d399";

  const previewHours = (parseInt(form.leave_after_mins,10)||120)/60;
  const previewMembers = parseInt(form.total_bots,10)||0;
  const previewCost = calcCost(previewMembers, previewHours, effectivePrice, formHasReaction ? reactionPrice : 0);

  function Pager({ page, total, set }) {
    if(total<=1)return null;
    return (
      <div style={{ display:"flex",alignItems:"center",gap:5 }}>
        {[["‹",-1],["›",1]].map(([sym,d])=>{const disabled=d===-1?page===0:page===total-1;return(<button key={sym} onClick={()=>!disabled&&set(p=>p+d)} style={{ background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.09)",color:"#64748b",borderRadius:6,width:28,height:28,cursor:disabled?"default":"pointer",fontSize:15,opacity:disabled?0.3:1,display:"flex",alignItems:"center",justifyContent:"center",padding:0 }}>{sym}</button>);})}
        <span style={{ fontSize:11,color:"#475569",minWidth:32,textAlign:"center" }}>{page+1}/{total}</span>
      </div>
    );
  }

  function saveList() {
    if(!listName.trim()||!form.custom_names.trim())return;
    const u={...savedLists,[listName.trim()]:form.custom_names};
    setSavedLists(u);localStorage.setItem("savedNameLists",JSON.stringify(u));
    setListName("");setListModal(false);
  }

  const [confirmForceClient, setConfirmForceClient] = useState(null);
  const [confirmStopClient, setConfirmStopClient] = useState(null);
  const doStop  = async id=>{ setConfirmStopClient(id); };
  const doStopConfirmed = async id=>{
    setConfirmStopClient(null);
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, status: "stopped", active_bots: 0, active: 0 } : c));
    try{await apiFetch(`/api/campaigns/${id}/stop`,{method:"POST"});}catch(e){setErr(e.message);}
  };
  const doForce = async id=>{ setConfirmForceClient(id); };
  const doForceConfirmed = async id=>{ setConfirmForceClient(null); try{await apiFetch(`/api/campaigns/${id}/force_exit`,{method:"POST"});load();}catch(e){setErr(e.message);} };
  const doDel   = async id=>{try{await apiFetch(`/api/campaigns/${id}`,{method:"DELETE"});setSuccess("Deleted.");setDelConfirm(null);load();}catch(e){setErr(e.message);setDelConfirm(null);}};
  const doCancel= async id=>{try{await apiFetch(`/api/campaigns/scheduled/${id}/cancel`,{method:"POST"});load();}catch(e){setErr(e.message);}};

  const pad = isMobile?"12px 14px":"22px 32px";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{font-family:'Outfit',sans-serif;background:#020408;-webkit-font-smoothing:antialiased;}
        ::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.07);border-radius:4px;}
        @keyframes ddOpen{from{opacity:0;transform:translateY(-6px);}to{opacity:1;transform:translateY(0);}}
        @keyframes floatUp{from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:translateY(0);}}
        .hov-card{transition:border-color .25s,box-shadow .25s,transform .2s;}
        .hov-row{transition:background .15s;}.hov-row:hover{background:rgba(99,179,237,0.045)!important;}
        .stat-card{transition:border-color .25s,box-shadow .25s,transform .22s;}.stat-card:hover{transform:translateY(-4px);}
        .stat-card[data-c="blue"]:hover{border-color:rgba(56,189,248,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(56,189,248,0.22)!important;}
        .stat-card[data-c="green"]:hover{border-color:rgba(74,222,128,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(74,222,128,0.18)!important;}
        .stat-card[data-c="amber"]:hover{border-color:rgba(251,191,36,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(251,191,36,0.18)!important;}
        .stat-card[data-c="purple"]:hover{border-color:rgba(192,132,252,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(192,132,252,0.18)!important;}
        .stat-card[data-c="red"]:hover{border-color:rgba(248,113,113,0.45)!important;box-shadow:0 22px 60px rgba(0,0,0,.7),0 0 40px rgba(248,113,113,0.18)!important;}
        input:focus,textarea:focus{border-color:rgba(99,179,237,0.55)!important;box-shadow:0 0 0 3px rgba(99,179,237,0.09)!important;outline:none;}
        input::placeholder,textarea::placeholder{color:rgba(148,163,184,0.4);}
        input[type=number]::-webkit-inner-spin-button{opacity:.3;}
        .abtn{transition:all .15s;}.abtn:hover{opacity:.88;transform:translateY(-1px);}
        .launch{transition:all .22s;}.launch:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 14px 40px rgba(56,189,248,0.45)!important;}.launch:active:not(:disabled){transform:translateY(0);}
        .smb:hover{background:rgba(255,255,255,.07)!important;color:#94a3b8!important;}
        @media(max-width:767px){.stat-grid{grid-template-columns:1fr 1fr!important;}.form-row-3{grid-template-columns:1fr!important;}.form-row-4{grid-template-columns:1fr 1fr!important;}.form-row-react{grid-template-columns:1fr!important;}.form-row-2col{grid-template-columns:1fr 1fr!important;}.launch-row{flex-direction:column!important;align-items:stretch!important;gap:12px!important;}.launch-row>button{min-width:unset!important;width:100%!important;}}
        @media(max-width:400px){.stat-grid{grid-template-columns:1fr 1fr!important;}.form-row-4{grid-template-columns:1fr!important;}}
        /* Broadcast Ticker */
        .bc-ticker-wrap{
          position:fixed;top:0;left:0;right:0;z-index:9999;
          background:linear-gradient(90deg,rgba(56,189,248,0.15),rgba(129,140,248,0.15));
          border-bottom:1px solid rgba(56,189,248,0.3);
          height:36px;overflow:hidden;display:flex;align-items:center;
          backdrop-filter:blur(8px);
        }
        .bc-ticker-label{
          flex-shrink:0;padding:0 16px;
          font-size:11px;font-weight:800;letter-spacing:1.5px;text-transform:uppercase;
          color:#38bdf8;border-right:1px solid rgba(56,189,248,0.3);
          white-space:nowrap;display:flex;align-items:center;gap:6px;
        }
        .bc-ticker-track{flex:1;overflow:hidden;position:relative;}
        .bc-ticker-inner{
          display:inline-flex;white-space:nowrap;
          animation:ticker-scroll 20s linear infinite;
        }
        .bc-ticker-inner:hover{animation-play-state:paused;}
        .bc-ticker-text{
          padding:0 60px;font-size:13px;font-weight:600;
          color:#e2e8f0;letter-spacing:.3px;
        }
        @keyframes ticker-scroll{
          0%{transform:translateX(0);}
          100%{transform:translateX(-50%);}
        }
      `}</style>


      <ConstellationBg/>
      {broadcast && (
        <div className="bc-ticker-wrap">
          <div className="bc-ticker-label">📡 Notice</div>
          <div className="bc-ticker-track">
            <div className="bc-ticker-inner">
              <span className="bc-ticker-text">{broadcast}</span>
              <span className="bc-ticker-text">{broadcast}</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ position:"relative",zIndex:1,minHeight:"100vh",fontFamily:"'Outfit',sans-serif",display:"flex",flexDirection:"column",marginTop:broadcast?36:0 }}>

        <header style={{ ...glass,borderRadius:0,borderLeft:"none",borderRight:"none",borderTop:"none",display:"flex",justifyContent:"space-between",alignItems:"center",padding:isMobile?"0 16px":"0 36px",height:isMobile?60:72,position:"sticky",top:0,zIndex:200 }}>
          <div style={{ display:"flex",alignItems:"center",gap:isMobile?10:14 }}>
            <div style={{ width:isMobile?36:44,height:isMobile?36:44,borderRadius:12,background:"linear-gradient(135deg,#38bdf8,#818cf8)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:isMobile?18:22,boxShadow:"0 0 26px rgba(56,189,248,.55), 0 0 8px rgba(129,140,248,.4)" }}>⚡</div>
            <div>
              <div style={{ fontSize:isMobile?17:22,fontWeight:900,letterSpacing:-.7,color:"#f1f5f9",lineHeight:1.1 }}>Zoom<span style={{color:"#38bdf8"}}>Members</span></div>
              {!isMobile&&<div style={{ fontSize:9,color:"#64748b",letterSpacing:2.8,textTransform:"uppercase",marginTop:2 }}>CLIENT PORTAL</div>}
            </div>
          </div>
          {!isMobile&&<div style={{ display:"flex",alignItems:"center",gap:8,background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.2)",borderRadius:30,padding:"7px 18px" }}>
            <span style={{ width:7,height:7,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 9px #4ade80",flexShrink:0,display:"inline-block" }}/>
            <span style={{ fontSize:12,color:"#4ade80",fontWeight:600,letterSpacing:.5 }}>System Live</span>
          </div>}
          <div style={{ display:"flex",alignItems:"center",gap:isMobile?8:12 }}>
            <div style={{ display:"flex",alignItems:"center",gap:9,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:32,padding:isMobile?"4px 10px 4px 6px":"5px 16px 5px 7px" }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#38bdf8,#818cf8)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff" }}>{uname[0]?.toUpperCase()}</div>
              {!isMobile&&<span style={{ fontSize:13,color:"#94a3b8",fontWeight:500 }}>{uname}</span>}
            </div>
            <button onClick={()=>{localStorage.clear();window.location.href="/login";}} style={{ background:"rgba(248,113,113,0.07)",border:"1px solid rgba(248,113,113,.28)",color:"#f87171",borderRadius:8,padding:isMobile?"6px 10px":"7px 18px",fontSize:isMobile?11:13,cursor:"pointer",fontFamily:"inherit",fontWeight:600,transition:"all .18s" }}>{isMobile?"Exit":"Sign Out"}</button>
          </div>
        </header>

        {err&&<div style={{ position:"fixed",top:isMobile?70:82,right:12,left:isMobile?12:"auto",zIndex:400,...glass,borderRadius:11,padding:"12px 16px",fontSize:13,color:"#f87171",borderColor:"rgba(248,113,113,.3)",maxWidth:isMobile?"100%":340,display:"flex",gap:10,alignItems:"center",animation:"floatUp .2s" }}><span style={{flex:1}}>{err}</span><span onClick={()=>setErr("")} style={{cursor:"pointer",opacity:.5,fontSize:16}}>✕</span></div>}
        {success&&<div style={{ position:"fixed",top:isMobile?70:82,right:12,left:isMobile?12:"auto",zIndex:400,...glass,borderRadius:11,padding:"12px 16px",fontSize:13,color:"#34d399",borderColor:"rgba(52,211,153,.3)",maxWidth:isMobile?"100%":340,display:"flex",gap:10,alignItems:"center",animation:"floatUp .2s" }}><span style={{flex:1}}>{success}</span><span onClick={()=>setSuccess("")} style={{cursor:"pointer",opacity:.5,fontSize:16}}>✕</span></div>}

        <div style={{ display:"flex",flex:1 }}>
          {/* ── Sidebar ── */}
          {!isMobile&&(
            <div style={{ width:220,flexShrink:0,borderRight:"1px solid rgba(255,255,255,.07)",background:"rgba(8,13,32,0.6)",backdropFilter:"blur(20px)",minHeight:"calc(100vh - 72px)",padding:"20px 12px",position:"sticky",top:72,height:"calc(100vh - 72px)",overflowY:"auto" }}>
              <div style={{ fontSize:9,color:"#475569",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:12,paddingLeft:8 }}>Navigation</div>
              {[
                { id:"dashboard", icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>, label:"Dashboard" },
                { id:"addfund", icon:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/><path d="M9 10h6M9 14h4"/></svg>, label:"Add Fund" },
              ].map(item=>(
                <button key={item.id} onClick={()=>setActivePage(item.id)} style={{ width:"100%",display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:700,marginBottom:4,transition:"all .15s",background:activePage===item.id?"linear-gradient(135deg,rgba(56,189,248,.15),rgba(129,140,248,.15))":"transparent",color:activePage===item.id?"#38bdf8":"#64748b",boxShadow:activePage===item.id?"inset 0 0 0 1px rgba(56,189,248,.25)":"none" }}>
                  <span style={{ fontSize:16 }}>{item.icon}</span>
                  {item.label}
                  {item.id==="addfund"&&fundHistory.filter(f=>f.status==="pending").length>0&&(
                    <span style={{ marginLeft:"auto",background:"#fbbf24",color:"#000",borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:800 }}>{fundHistory.filter(f=>f.status==="pending").length}</span>
                  )}
                </button>
              ))}

            </div>
          )}

          {/* ── Main content ── */}
          <div style={{ flex:1,minWidth:0 }}>
          {/* Mobile bottom nav */}
          {isMobile&&(
            <div style={{ position:"fixed",bottom:0,left:0,right:0,zIndex:200,background:"rgba(8,13,32,0.95)",backdropFilter:"blur(20px)",borderTop:"1px solid rgba(255,255,255,.07)",display:"flex",padding:"8px 0" }}>
              {[{id:"dashboard",icon:"⊞",label:"Dashboard"},{id:"addfund",icon:"₹",label:"Add Fund"}].map(item=>(
                <button key={item.id} onClick={()=>setActivePage(item.id)} style={{ flex:1,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:"6px 0",color:activePage===item.id?"#38bdf8":"#475569",transition:"color .15s" }}>
                  <span style={{ fontSize:20 }}>{item.icon}</span>
                  <span style={{ fontSize:9,fontWeight:700,letterSpacing:.5 }}>{item.label.toUpperCase()}</span>
                </button>
              ))}
            </div>
          )}

        {activePage==="dashboard"&&<div style={{ padding:pad,maxWidth:1440,margin:"0 auto" }}>

          <div className="stat-grid" style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:isMobile?10:16,marginBottom:isMobile?14:22 }}>
            {[
              { icon:"◈", label:"Meetings",      val: activeCampaigns.length,                                                           c:"#38bdf8", k:"blue"   },
              { icon:"●", label:"Total Members",  val: totalMembers,                                                                     c:"#4ade80", k:"green"  },
              { icon:"◷", label:"Scheduled",      val: waitingSchedCount,                                                                c:"#fbbf24", k:"amber"  },
              { icon:"◎", label:"Balance",         val: fmtBal(bal, cur),                                                                c: balColor, k: bal<0?"red":bal<50?"amber":"green", isText:true },
              { icon:"◉", label:"Total Spend",     val: fmtBal(totalSpend, cur),                                                         c:"#f87171", k:"red",   isText:true },
            ].map((s,i)=>(
              <div key={i} className="stat-card" data-c={s.k} style={{ ...glass,padding:isMobile?"16px 14px 13px":"24px 24px 20px",position:"relative",overflow:"hidden",animation:`floatUp .4s ease ${i*.07}s both` }}>
                <div style={{ position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,transparent,${s.c},transparent)`,boxShadow:`0 0 16px ${s.c}80` }}/>
                <div style={{ fontSize:s.isText?(isMobile?16:22):isMobile?36:52,fontWeight:900,color:s.c,letterSpacing:s.isText?-0.5:-3,lineHeight:1,marginBottom:isMobile?5:8,wordBreak:"break-all" }}>{s.val}</div>
                <div style={{ fontSize:isMobile?9:12,color:"#f1f5f9",letterSpacing:isMobile?1:2.2,textTransform:"uppercase",fontWeight:800 }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div className="hov-card" style={{ ...glass,marginBottom:isMobile?14:22,overflow:"visible" }}>
            <div style={{ padding:isMobile?"13px 16px":"16px 26px",borderBottom:"1px solid rgba(255,255,255,0.07)",display:"flex",alignItems:"center",gap:12,background:"rgba(56,189,248,0.025)",borderRadius:"16px 16px 0 0" }}>
              <div style={{ width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#38bdf8,#818cf8)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,boxShadow:"0 0 18px rgba(56,189,248,.4)" }}>✦</div>
              <div>
                <div style={{ fontSize:isMobile?15:17,fontWeight:800,color:"#f1f5f9",letterSpacing:-.4 }}>New Task</div>
                <div style={{ fontSize:9,color:"#64748b",letterSpacing:2.2,textTransform:"uppercase" }}>CONFIGURE & LAUNCH</div>
              </div>
            </div>

            <div style={{ padding:isMobile?"16px 14px":"24px 26px" }}>
              <div className="form-row-3" style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:16 }}>
                <div><label style={lbl}>Task Name</label><input style={inp} placeholder="e.g. Monday Webinar" value={form.name} onChange={e=>F("name",e.target.value)}/></div>
                <div><label style={lbl}>Meeting URL / ID</label><input style={inp} placeholder="https://zoom.us/j/... or ID" value={form.zoom_url} onChange={e=>F("zoom_url",e.target.value)}/></div>
                <div><label style={lbl}>Password (optional)</label><input style={inp} placeholder="Leave blank if none" value={form.passcode} onChange={e=>F("passcode",e.target.value)}/></div>
              </div>

              <div className="form-row-4" style={{ display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:16,marginBottom:16 }}>
                <div><label style={lbl}>Member Names</label><Dropdown options={NAME_OPTIONS} value={form.name_type} onChange={v=>F("name_type",v)}/></div>
                <div>
                  <label style={lbl}>Total Members</label>
                  <input type="number" style={inp} min={10} step={10} value={form.total_bots} onChange={e=>F("total_bots",e.target.value)} onBlur={e=>{const v=parseInt(e.target.value,10)||10;F("total_bots",Math.max(10,Math.round(v/10)*10));}}/>
                  <div style={{ fontSize:10,color:"#4a6280",marginTop:3 }}>Min 10, multiples of 10</div>
                </div>
                <div>
                  <label style={lbl}>Leave After</label>
                  <div style={{ display:"flex",gap:6 }}>
                    {[120,180,240,300].map(mins=>(
                      <button key={mins} onClick={()=>F("leave_after_mins",mins)} style={{ flex:1,padding:"9px 4px",borderRadius:9,border:"1px solid",borderColor:form.leave_after_mins===mins?"rgba(56,189,248,0.6)":"rgba(255,255,255,0.13)",background:form.leave_after_mins===mins?"rgba(56,189,248,0.15)":"rgba(255,255,255,0.06)",color:form.leave_after_mins===mins?"#38bdf8":"#94a3b8",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>
                        {mins/60}h
                      </button>
                    ))}
                  </div>
                </div>
                <div><label style={lbl}>Meeting Type</label><Dropdown options={MEETING_OPTIONS} value={form.meeting_type} onChange={v=>F("meeting_type",v)}/></div>
              </div>

              <div style={{ marginBottom:16 }}>
                <Toggle label="🐢 Slow Join (spread members over time)" checked={form.slow_join} onChange={v=>F("slow_join",v)}/>
                {form.slow_join&&(
                  <div style={{ background:"rgba(255,255,255,.025)",border:"1px solid rgba(255,255,255,.07)",borderRadius:10,padding:14,marginTop:10 }}>
                    <div className="form-row-2col" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
                      <div>
                        <label style={lbl}>Spread Over (minutes)</label>
                        <input type="number" style={inp} min={1} step={1} placeholder="e.g. 10" value={form.custom_spread_mins} onChange={e=>F("custom_spread_mins",e.target.value)}/>
                      </div>
                      <div style={{ display:"flex",alignItems:"flex-end" }}>
                        <div style={{ fontSize:11,color:"#94a3b8",lineHeight:1.5 }}>
                          {(+form.custom_spread_mins>0 && +form.total_bots>0)
                            ? <>≈ {( (+form.custom_spread_mins*60) / +form.total_bots ).toFixed(1)}s gap/member — all {form.total_bots} members join gradually over {form.custom_spread_mins} min instead of all at once.</>
                            : <>Enter minutes — e.g. 10 for "50 members over 10 min".</>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {form.name_type==="custom"&&(
                <div style={{ marginBottom:16 }}>
                  <label style={lbl}>Custom Names</label>
                  <textarea style={{ ...inp,height:78,resize:"vertical" }} placeholder={"John Doe\nJane Smith\n..."} value={form.custom_names} onChange={e=>F("custom_names",e.target.value)}/>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:6 }}>
                    <span style={{ fontSize:10,color:"#4a6280" }}>{form.custom_names.split(/[\n,]/).filter(n=>n.trim()).length} names</span>
                    <div style={{ display:"flex",gap:6 }}><Smb onClick={()=>setListModal("load")}>📁 Load</Smb><Smb onClick={()=>{setListName("");setListModal("save");}}>💾 Save</Smb></div>
                  </div>
                </div>
              )}

              <div style={{ height:1,background:"rgba(255,255,255,.06)",marginBottom:20 }}/>

              {userProfile.reactions_enabled && (
                <div className="form-row-react" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:20 }}>
                  <div>
                    <Toggle label="🎉 Floating Emoji Reactions" checked={form.floating_emoji} onChange={v=>F("floating_emoji",v)}/>
                    {form.floating_emoji&&(
                      <div style={{ background:"rgba(255,255,255,.025)",border:"1px solid rgba(255,255,255,.07)",borderRadius:10,padding:14,marginTop:10 }}>
                        <div style={{ fontSize:9,color:"#f1f5f9",letterSpacing:1.4,textTransform:"uppercase",fontWeight:800,marginBottom:4 }}>Pick Emojis</div>
                        <Chips items={FLOAT_EMOJIS} selected={form.float_emoji_keys} onToggle={v=>toggleArr("float_emoji_keys",v)}/>
                        <div className="form-row-2col" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:12 }}>
                          <div><label style={lbl}>Interval (sec)</label><input type="number" style={inp} min={0} value={form.float_interval} onChange={e=>F("float_interval",e.target.value)}/></div>
                          <div><label style={lbl}>Count (0 = rnd)</label><input type="number" style={inp} min={0} value={form.float_count} onChange={e=>F("float_count",e.target.value)}/></div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <Toggle label="📢 Participant Reactions" checked={form.participant_reactions} onChange={v=>F("participant_reactions",v)}/>
                    {form.participant_reactions&&(
                      <div style={{ background:"rgba(255,255,255,.025)",border:"1px solid rgba(255,255,255,.07)",borderRadius:10,padding:14,marginTop:10 }}>
                        <div style={{ fontSize:9,color:"#f1f5f9",letterSpacing:1.4,textTransform:"uppercase",fontWeight:800,marginBottom:4 }}>Reactions</div>
                        <Chips items={PART_REACTIONS} selected={form.part_reaction_keys} onToggle={v=>toggleArr("part_reaction_keys",v)}/>
                        <div className="form-row-2col" style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:12 }}>
                          <div><label style={lbl}>Count/Wave</label><input type="number" style={inp} min={0} value={form.part_count} onChange={e=>F("part_count",e.target.value)}/></div>
                          <div><label style={lbl}>Interval (sec)</label><input type="number" style={inp} min={0} value={form.part_interval} onChange={e=>F("part_interval",e.target.value)}/></div>
                          <div><label style={lbl}>Remove After (sec)</label><input type="number" style={inp} min={0} value={form.part_remove_after} onChange={e=>F("part_remove_after",e.target.value)}/></div>
                          <div><label style={lbl}>Start After (min)</label><input type="number" style={inp} min={0} value={form.part_start_after} onChange={e=>F("part_start_after",e.target.value)}/></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ height:1,background:"rgba(255,255,255,.06)",marginBottom:20 }}/>

              {previewMembers>0&&previewHours>0&&(
                <div style={{ background:"rgba(56,189,248,0.06)",border:"1px solid rgba(56,189,248,0.18)",borderRadius:10,padding:"12px 16px",marginBottom:20,display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:10 }}>
                  <div style={{ fontSize:12,color:"#94a3b8" }}>
                    <span style={{ color:"#38bdf8",fontWeight:700 }}>{previewMembers} members</span>
                    {" × "}
                    <span style={{ color:"#38bdf8",fontWeight:700 }}>{previewHours}h</span>
                    {formHasReaction && reactionPrice > 0 && <span style={{ marginLeft:6,fontSize:11,color:"#a78bfa",fontWeight:700 }}>· Reaction rate</span>}
                    {" = "}
                  </div>
                  <div style={{ fontSize:16,fontWeight:800,color:"#38bdf8" }}>
                    {fmtBal(previewCost, cur)}
                  </div>
                  <div style={{ fontSize:10,color:"#4a6280",width:"100%" }}>
                    Estimated charge for this task · Balance after: <span style={{ color: (bal-previewCost)<0?"#f87171":"#34d399",fontWeight:700 }}>{fmtBal(bal-previewCost,cur)}</span>
                  </div>
                </div>
              )}

              <div className="launch-row" style={{ display:"flex",alignItems:"flex-end",gap:22 }}>
                <div style={{ flex:1 }}>
                  <Toggle label="📅 Schedule Task (IST)" checked={form.schedule_enabled} onChange={v=>F("schedule_enabled",v)}/>
                  {form.schedule_enabled&&<input type="datetime-local" style={{ ...inp,marginTop:10,maxWidth:isMobile?"100%":340 }} value={form.schedule_at} onChange={e=>F("schedule_at",e.target.value)}/>}
                </div>
                <button className="launch" onClick={submitCampaign} disabled={submitting||cooldown>0||(!form.schedule_enabled && previewCost > 0 && bal < previewCost)} style={{ flexShrink:0,minWidth:210,background:(submitting||cooldown>0||(!form.schedule_enabled && previewCost > 0 && bal < previewCost))?"rgba(100,116,139,.4)":"linear-gradient(135deg,#38bdf8,#818cf8)",color:"#fff",border:"none",borderRadius:11,padding:"14px 30px",fontSize:15,fontWeight:700,cursor:(submitting||cooldown>0)?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:.4,boxShadow:"0 4px 24px rgba(56,189,248,.3)" }}>
                  {submitting?"Launching…":cooldown>0?`⏳ Wait ${cooldown}s`:form.schedule_enabled?"⏰  Schedule Task":"▶  Launch Task"}
                </button>
              </div>
            </div>
          </div>

          <div className="hov-card" style={{ ...glass,overflow:"hidden",marginBottom:isMobile?14:18 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"15px 18px",borderBottom:"1px solid rgba(255,255,255,.07)" }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <span style={{ width:9,height:9,borderRadius:"50%",background:"#38bdf8",boxShadow:"0 0 12px #38bdf8",flexShrink:0,display:"inline-block" }}/>
                <span style={{ fontSize:15,fontWeight:800,color:"#cbd5e1" }}>All Meetings</span>
                {activeCampaigns.length>0&&<span style={{ background:"rgba(255,255,255,.07)",color:"#94a3b8",border:"1px solid rgba(255,255,255,.09)",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700 }}>{activeCampaigns.length}</span>}
              </div>
              <Pager page={cPage} total={Math.ceil(activeCampaigns.length/PS)} set={setCPage}/>
            </div>
            {activeCampaigns.length===0
              ?<Empty>No meetings yet — launch a task above ↑</Empty>
              :isMobile
                ?pagedC.map(c=><CampCard key={c.id} c={c} onStop={()=>doStop(c.id)} onForce={()=>doForce(c.id)} stopReason={stopReasons[c.id]}/>)
                :<div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%",borderCollapse:"collapse",minWidth:750 }}>
                    <thead><tr style={{ borderBottom:"1px solid rgba(255,255,255,.07)" }}>
                      {["Task Name","URL / ID","Qty","Start Time","Leave After","Name","Type","React","Status"].map(h=>(
                        <th key={h} style={{ padding:"9px 14px",textAlign:"left",fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:1,color:"#f1f5f9",whiteSpace:"nowrap",textShadow:"0 0 8px rgba(241,245,249,0.6)" }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>{pagedC.map(c=><CampRow key={c.id} c={c} onStop={()=>doStop(c.id)} onForce={()=>doForce(c.id)} reactionsEnabled={userProfile.reactions_enabled} stopReason={stopReasons[c.id]}/>)}</tbody>
                  </table>
                </div>
            }
          </div>

          <div className="hov-card" style={{ ...glass,overflow:"hidden",marginBottom:isMobile?14:18 }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"15px 18px",borderBottom:"1px solid rgba(255,255,255,.07)" }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <span style={{ width:9,height:9,borderRadius:"50%",background:"#fbbf24",boxShadow:"0 0 12px #fbbf24",flexShrink:0,display:"inline-block" }}/>
                <span style={{ fontSize:15,fontWeight:800,color:"#cbd5e1" }}>Scheduled Tasks</span>
                {activeSched.length>0&&<span style={{ background:"rgba(255,255,255,.07)",color:"#94a3b8",border:"1px solid rgba(255,255,255,.09)",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700 }}>{activeSched.length}</span>}
              </div>
            </div>
            {activeSched.length===0
              ?<Empty>No scheduled tasks</Empty>
              :activeSched.map(s=>(
                <div key={s.id} className="hov-row" style={{ padding:"13px 18px",borderBottom:"1px solid rgba(255,255,255,0.045)",display:"flex",justifyContent:"space-between",alignItems:isMobile?"flex-start":"center",flexDirection:isMobile?"column":"row",gap:10 }}>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:"#cbd5e1",marginBottom:4 }}>{s.campaign_name}</div>
                    <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:3 }}>
                      <div style={{ fontSize:11,color:"#4a6280",fontFamily:"'JetBrains Mono',monospace" }}>{fmtTime(s.scheduled_at)}</div>
                      {s.total_bots&&<span style={{ fontSize:11,color:"#38bdf8",background:"rgba(56,189,248,.08)",border:"1px solid rgba(56,189,248,.22)",padding:"2px 8px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace" }}>👥 {s.total_bots} members</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
                    {s.status==="cancelled"
                      ?<span style={{ fontSize:11,color:"#f87171",background:"rgba(248,113,113,.08)",border:"1px solid rgba(248,113,113,.22)",padding:"3px 9px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace" }}>✕ Cancelled</span>
                      :<>
                        <span style={{ fontSize:11,color:"#fbbf24",background:"rgba(251,191,36,.08)",border:"1px solid rgba(251,191,36,.22)",padding:"3px 9px",borderRadius:20,fontFamily:"'JetBrains Mono',monospace" }}>⏱ {fmtCountdown(s.scheduled_at)}</span>
                        {(s.scheduled_at * 1000 - Date.now()) > 7 * 60 * 1000 && <button onClick={()=>doCancel(s.id)} style={{ background:"transparent",border:"1px solid rgba(248,113,113,.25)",color:"#f87171",borderRadius:7,padding:"4px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>}
                      </>
                    }
                  </div>
                </div>
              ))
            }
          </div>

          <div className="hov-card" style={{ ...glass,overflow:"hidden" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"15px 18px",borderBottom:"1px solid rgba(255,255,255,.07)" }}>
              <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                <span style={{ width:9,height:9,borderRadius:"50%",background:"#6366f1",boxShadow:"0 0 12px #6366f1",flexShrink:0,display:"inline-block" }}/>
                <span style={{ fontSize:15,fontWeight:800,color:"#cbd5e1" }}>Previous Tasks</span>
                {history.length>0&&<span style={{ background:"rgba(255,255,255,.07)",color:"#94a3b8",border:"1px solid rgba(255,255,255,.09)",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700 }}>{history.length}</span>}
              </div>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <Pager page={hPage} total={Math.ceil(history.length/PS)} set={setHPage}/>
                {history.length>0 && <DownloadBtn data={history} filename="task_history.csv" price={price} reactionPricePerMember={reactionPrice}/>}
              </div>
            </div>
            {history.length===0
              ?<Empty>No history yet</Empty>
              :isMobile
                ?<>{pagedH.map(h=><HistoryCard key={h.id} h={h} pricePerMember={price} reactionPricePerMember={reactionPrice} currency={cur}/>)}</>
                :<div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%",borderCollapse:"collapse",minWidth:800 }}>
                    <thead><tr style={{ borderBottom:"1px solid rgba(255,255,255,.07)" }}>
                      {["Task Name","URL / ID","Sent","Start Time","Duration","Name","Type","React","Status","Charge"].map(col=>(
                        <th key={col} style={{ padding:"9px 14px",textAlign:"left",fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:1,color:"#f1f5f9",whiteSpace:"nowrap",textShadow:"0 0 8px rgba(241,245,249,0.6)" }}>{col}</th>
                      ))}
                    </tr></thead>
                    <tbody>{pagedH.map(h=><HistoryRow key={h.id} h={h} pricePerMember={price} reactionPricePerMember={reactionPrice} currency={cur} reactionsEnabled={userProfile.reactions_enabled}/>)}</tbody>
                  </table>
                </div>
            }
          </div>

        </div>}

        {/* ── Add Fund Page ── */}
        {activePage==="addfund"&&(
          <div style={{ padding:pad,maxWidth:1100,margin:"0 auto" }}>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:22,fontWeight:900,color:"#f1f5f9",marginBottom:4,display:"flex",alignItems:"center",gap:10 }}><span style={{ width:36,height:36,borderRadius:10,background:"linear-gradient(135deg,#38bdf8,#4ade80)",display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0 }}>₹</span>Add Fund</div>
              <div style={{ fontSize:13,color:"#f1f5f9",fontWeight:700 }}>Select a payment method, scan the QR, and submit your transaction details.</div>
              {payInstr&&<div style={{ marginTop:12,padding:"16px 20px",background:"rgba(56,189,248,.07)",border:"1px solid rgba(56,189,248,.25)",borderRadius:12,borderLeft:"4px solid #38bdf8",width:"100%" }}>
                <div style={{ fontSize:11,fontWeight:900,color:"#38bdf8",letterSpacing:2,textTransform:"uppercase",marginBottom:10 }}>📋 Payment Instructions</div>
                <pre style={{ fontSize:14,color:"#f1f5f9",lineHeight:2,whiteSpace:"pre-wrap",fontFamily:"inherit",margin:0,fontWeight:700 }}>{payInstr}</pre>
              </div>}
            </div>
            {fundErr&&<div style={{ background:"rgba(248,113,113,.08)",border:"1px solid rgba(248,113,113,.25)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#f87171",marginBottom:12 }}>{fundErr}</div>}
            {fundSuccess&&<div style={{ background:"rgba(74,222,128,.08)",border:"1px solid rgba(74,222,128,.25)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#4ade80",marginBottom:12 }}>{fundSuccess}</div>}
            <div className="hov-card" style={{ ...glass,padding:24,marginBottom:16 }}>
              {qrList.length===0
                ?<div style={{ textAlign:"center",color:"#64748b",padding:"30px 0",fontSize:13 }}>No payment methods available. Please contact admin.</div>
                :<>
                  <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:12 }}>Select Payment Method</div>
                  <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:20 }}>
                    {qrList.map(q=>(
                      <button key={q.id} onClick={()=>setSelQr(q)} style={{ background:selQr?.id===q.id?"rgba(56,189,248,.15)":"rgba(255,255,255,.04)",border:`1px solid ${selQr?.id===q.id?"#38bdf8":"rgba(255,255,255,.1)"}`,color:selQr?.id===q.id?"#38bdf8":"#94a3b8",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all .15s" }}>{q.label}</button>
                    ))}
                  </div>
                  {selQr&&(
                    <div style={{ textAlign:"center",marginBottom:24 }}>
                      <div style={{ fontSize:11,color:"#64748b",marginBottom:12,letterSpacing:.5 }}>Scan QR to pay via {selQr.label}</div>
                      <img src={selQr.image_base64.startsWith("data:")?selQr.image_base64:`data:image/png;base64,${selQr.image_base64}`} alt={selQr.label} style={{ width:340,height:340,maxWidth:"90%",borderRadius:14,border:"2px solid rgba(56,189,248,.3)",objectFit:"contain",background:"#fff",padding:12,boxShadow:"0 0 30px rgba(56,189,248,.15)" }}/>
                    </div>
                  )}
                  <div style={{ display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14,marginBottom:18 }}>
                    <div>
                      <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>Transaction / UTR ID</div>
                      <input value={txnId} onChange={e=>setTxnId(e.target.value)} placeholder="e.g. T2506261234567" style={{ width:"100%",background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:8,padding:"11px 13px",fontSize:13,color:"#f1f5f9",fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>
                    </div>
                    <div>
                      <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>Amount (₹)</div>
                      <input type="number" value={fundAmt} onChange={e=>setFundAmt(e.target.value)} placeholder="e.g. 500" min="100" style={{ width:"100%",background:"rgba(255,255,255,.05)",border:`1px solid ${fundAmt && parseFloat(fundAmt) < 100 ? "rgba(248,113,113,.6)" : "rgba(255,255,255,.1)"}`,borderRadius:8,padding:"11px 13px",fontSize:13,color:"#f1f5f9",fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>
                      {fundAmt && parseFloat(fundAmt) < 100 && <div style={{ fontSize:11,color:"#f87171",marginTop:5 }}>⚠ Minimum amount is ₹100</div>}
                    </div>
                  </div>
                  <button disabled={fundSubmitting||!selQr||!txnId.trim()||!fundAmt||parseFloat(fundAmt)<100} onClick={async()=>{
                    setFundErr(""); setFundSuccess(""); setFundSubmitting(true);
                    try {
                      await apiFetch("/api/payments/request",{method:"POST",body:JSON.stringify({qr_id:selQr.id,transaction_id:txnId.trim(),amount:parseFloat(fundAmt),payment_method:selQr.label})});
                      setFundSuccess("Request submitted! Pending admin approval.");
                      setTxnId(""); setFundAmt(""); setSelQr(null);
                      const fh = await apiFetch("/api/payments/history").catch(()=>[]);
                      setFundHistory(fh||[]);
                    } catch(e){ setFundErr(e.message||"Submission failed."); }
                    setFundSubmitting(false);
                  }} style={{ width:"100%",background:fundSubmitting||!selQr||!txnId.trim()||!fundAmt?"rgba(100,116,139,.3)":"linear-gradient(135deg,#38bdf8,#4ade80)",color:"#fff",border:"none",borderRadius:10,padding:"14px",fontSize:14,fontWeight:800,cursor:fundSubmitting||!selQr||!txnId.trim()||!fundAmt?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:.4,transition:"all .2s" }}>
                    {fundSubmitting?"Submitting...":"Submit Payment Request"}
                  </button>
                </>
              }
            </div>

            <div className="hov-card" style={{ ...glass,overflow:"hidden" }}>
              <div style={{ padding:"14px 18px",borderBottom:"1px solid rgba(255,255,255,.07)",display:"flex",alignItems:"center",gap:10 }}>
                <span style={{ width:8,height:8,borderRadius:"50%",background:"#6366f1",boxShadow:"0 0 10px #6366f1",display:"inline-block" }}/>
                <span style={{ fontSize:14,fontWeight:800,color:"#cbd5e1" }}>Fund History</span>
                {fundHistory.filter(f=>f.status==="pending").length>0&&<span style={{ background:"rgba(251,191,36,.12)",color:"#fbbf24",border:"1px solid rgba(251,191,36,.25)",borderRadius:20,padding:"2px 9px",fontSize:11,fontWeight:700 }}>{fundHistory.filter(f=>f.status==="pending").length} pending</span>}
                <span style={{ fontSize:11,color:"#475569" }}>{fundHistory.filter(f=>f.type!=="debit").length} total</span>
                {fundHistory.length>0 && <FundDownloadBtn data={fundHistory.filter(f=>f.type!=="debit")} />}
              </div>
              {fundHistory.length===0
                ?<Empty>No fund requests yet</Empty>
                :(<>
                  <div style={{ overflowX:"auto" }}>
                    <table style={{ width:"100%",borderCollapse:"collapse" }}>
                      <thead><tr style={{ borderBottom:"1px solid rgba(255,255,255,.07)" }}>
                        {["Date","Method","TXN ID","Amount","Status"].map(col=>(
                          <th key={col} style={{ padding:"9px 14px",textAlign:"left",fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:1,color:"#f1f5f9",whiteSpace:"nowrap" }}>{col}</th>
                        ))}
                      </tr></thead>
                      <tbody>{fundHistory.filter(f=>f.type!=="debit").slice(histPage*5,(histPage+1)*5).map(f=>(
                        <tr key={f.id} style={{ borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                          <td style={TDstyle}><span style={{ fontSize:11,color:"#94a3b8" }}>{new Date(f.created_at*1000).toLocaleString("en-IN",{timeZone:"Asia/Kolkata",day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</span></td>
                          <td style={TDstyle}><span style={{ fontSize:12,fontWeight:600,color:"#cbd5e1" }}>{f.payment_method||"—"}</span></td>
                          <td style={TDstyle}><span style={{ fontSize:11,fontFamily:"'JetBrains Mono',monospace",color:"#94a3b8" }}>{f.transaction_id||"—"}</span></td>
                          <td style={TDstyle}><span style={{ fontSize:13,fontWeight:800,color:f.type==="debit"?"#f87171":"#4ade80" }}>{f.type==="debit"?"-":"+"}{fmtBal(f.amount, userProfile.display_currency)}</span></td>
                          <td style={TDstyle}><span style={{ fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:f.status==="completed"?"rgba(74,222,128,.1)":f.status==="pending"?"rgba(251,191,36,.1)":"rgba(248,113,113,.1)",color:f.status==="completed"?"#4ade80":f.status==="pending"?"#fbbf24":"#f87171",border:`1px solid ${f.status==="completed"?"rgba(74,222,128,.25)":f.status==="pending"?"rgba(251,191,36,.25)":"rgba(248,113,113,.25)"}` }}>{f.status}</span></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  {fundHistory.length>5&&(
                    <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",borderTop:"1px solid rgba(255,255,255,.06)" }}>
                      <button onClick={()=>setHistPage(p=>Math.max(0,p-1))} disabled={histPage===0} style={{ background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.09)",color:histPage===0?"#334155":"#94a3b8",borderRadius:7,padding:"5px 14px",fontSize:12,cursor:histPage===0?"not-allowed":"pointer",fontFamily:"inherit" }}>← Prev</button>
                      <span style={{ fontSize:12,color:"#475569" }}>Page {histPage+1} of {Math.ceil(fundHistory.length/5)}</span>
                      <button onClick={()=>setHistPage(p=>Math.min(Math.ceil(fundHistory.length/5)-1,p+1))} disabled={(histPage+1)*5>=fundHistory.length} style={{ background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.09)",color:(histPage+1)*5>=fundHistory.length?"#334155":"#94a3b8",borderRadius:7,padding:"5px 14px",fontSize:12,cursor:(histPage+1)*5>=fundHistory.length?"not-allowed":"pointer",fontFamily:"inherit" }}>Next →</button>
                    </div>
                  )}
                </>)
              }
            </div>
            {isMobile&&<div style={{ height:70 }}/>}
          </div>
        )}
        </div>
        </div>
      </div>

      {delConfirm&&(
        <Modal onClose={()=>setDelConfirm(null)}>
          <div style={{ fontSize:15,fontWeight:700,color:"#f1f5f9",marginBottom:10 }}>🗑️ Delete Campaign</div>
          <div style={{ fontSize:13,color:"#64748b",marginBottom:22,lineHeight:1.7 }}>This campaign will be permanently deleted. Are you sure?</div>
          <div style={{ display:"flex",gap:10 }}>
            <button onClick={()=>doDel(delConfirm)} style={{ flex:1,background:"linear-gradient(135deg,#dc2626,#ef4444)",color:"#fff",border:"none",borderRadius:9,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>Delete</button>
            <button onClick={()=>setDelConfirm(null)} style={{ flex:1,background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.09)",color:"#64748b",borderRadius:9,padding:"11px",fontSize:13,cursor:"pointer",fontFamily:"inherit" }}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ── Add Fund Modal ── */}
      {fundModal&&(
        <div onClick={e=>e.target===e.currentTarget&&setFundModal(false)} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.88)",backdropFilter:"blur(12px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:"16px" }}>
          <div style={{ background:"rgba(8,13,32,0.95)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:18,boxShadow:"0 8px 48px rgba(0,0,0,0.7)",padding:26,width:"100%",maxWidth:460,maxHeight:"90vh",overflowY:"auto",animation:"floatUp .18s ease" }}>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20 }}>
              <div style={{ fontSize:18,fontWeight:800,color:"#f1f5f9" }}>💰 Add Fund</div>
              <span onClick={()=>setFundModal(false)} style={{ cursor:"pointer",color:"#64748b",fontSize:20,lineHeight:1 }}>✕</span>
            </div>
            {fundErr&&<div style={{ background:"rgba(248,113,113,.08)",border:"1px solid rgba(248,113,113,.25)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#f87171",marginBottom:12 }}>{fundErr}</div>}
            {fundSuccess&&<div style={{ background:"rgba(74,222,128,.08)",border:"1px solid rgba(74,222,128,.25)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#4ade80",marginBottom:12 }}>{fundSuccess}</div>}
            {qrList.length===0
              ?<div style={{ textAlign:"center",color:"#64748b",padding:"30px 0",fontSize:13 }}>No payment methods available.<br/>Please contact admin.</div>
              :<>
                <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10 }}>Select Payment Method</div>
                <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:18 }}>
                  {qrList.map(q=>(
                    <button key={q.id} onClick={()=>setSelQr(q)} style={{ background:selQr?.id===q.id?"rgba(56,189,248,.15)":"rgba(255,255,255,.04)",border:`1px solid ${selQr?.id===q.id?"#38bdf8":"rgba(255,255,255,.1)"}`,color:selQr?.id===q.id?"#38bdf8":"#94a3b8",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",transition:"all .15s" }}>{q.label}</button>
                  ))}
                </div>
                {selQr&&(
                  <div style={{ textAlign:"center",marginBottom:18 }}>
                    <div style={{ fontSize:11,color:"#64748b",marginBottom:10,letterSpacing:.5 }}>Scan QR to pay</div>
                    <img src={selQr.image_base64.startsWith("data:")?selQr.image_base64:`data:image/png;base64,${selQr.image_base64}`} alt={selQr.label} style={{ width:200,height:200,borderRadius:12,border:"1px solid rgba(255,255,255,.1)",objectFit:"contain",background:"#fff",padding:6 }}/>
                  </div>
                )}
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>Transaction ID</div>
                  <input value={txnId} onChange={e=>setTxnId(e.target.value)} placeholder="Enter UTR / Transaction ID" style={{ width:"100%",background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.1)",borderRadius:8,padding:"10px 13px",fontSize:13,color:"#f1f5f9",fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>
                </div>
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1,textTransform:"uppercase",marginBottom:6 }}>Amount (₹)</div>
                  <input type="number" value={fundAmt} onChange={e=>setFundAmt(e.target.value)} placeholder="Enter amount paid" min="100" style={{ width:"100%",background:"rgba(255,255,255,.05)",border:`1px solid ${fundAmt && parseFloat(fundAmt) < 100 ? "rgba(248,113,113,.6)" : "rgba(255,255,255,.1)"}`,borderRadius:8,padding:"10px 13px",fontSize:13,color:"#f1f5f9",fontFamily:"inherit",outline:"none",boxSizing:"border-box" }}/>
                <div style={{ fontSize:11,color:"#f87171",marginTop:5,minHeight:16 }}>{fundAmt && parseFloat(fundAmt) < 100 ? "⚠ Minimum amount is ₹100" : ""}</div>
                </div>
                <button disabled={fundSubmitting||!selQr||!txnId.trim()||!fundAmt} onClick={async()=>{
                  setFundErr(""); setFundSuccess(""); setFundSubmitting(true);
                  try {
                    await apiFetch("/api/payments/request",{method:"POST",body:JSON.stringify({qr_id:selQr.id,transaction_id:txnId.trim(),amount:parseFloat(fundAmt),payment_method:selQr.label})});
                    setFundSuccess("Request submitted! Pending admin approval.");
                    setTxnId(""); setFundAmt(""); setSelQr(null);
                    const fh = await apiFetch("/api/payments/history").catch(()=>[]);
                    setFundHistory(fh||[]);
                  } catch(e){ setFundErr(e.message||"Submission failed"); }
                  setFundSubmitting(false);
                }} style={{ width:"100%",background:fundSubmitting||!selQr||!txnId.trim()||!fundAmt?"rgba(100,116,139,.3)":"linear-gradient(135deg,#38bdf8,#4ade80)",color:"#fff",border:"none",borderRadius:10,padding:"13px",fontSize:14,fontWeight:800,cursor:fundSubmitting||!selQr||!txnId.trim()||!fundAmt?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:.4,transition:"all .2s" }}>
                  {fundSubmitting?"Submitting...":"Submit Request"}
                </button>
              </>
            }
            {/* Fund History inside modal */}
            {fundHistory.length>0&&(
              <div style={{ marginTop:24,borderTop:"1px solid rgba(255,255,255,.07)",paddingTop:18 }}>
                <div style={{ fontSize:11,color:"#94a3b8",fontWeight:700,letterSpacing:1.5,textTransform:"uppercase",marginBottom:12 }}>Your Fund History</div>
                {fundHistory.filter(f=>f.type!=="debit").map(f=>(
                  <div key={f.id} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                    <div>
                      <div style={{ fontSize:13,fontWeight:700,color:f.type==="debit"?"#f87171":"#4ade80" }}>{f.type==="debit"?"-":"+"}{fmtBal(f.amount, userProfile.display_currency)}</div>
                      <div style={{ fontSize:10,color:"#64748b",marginTop:2 }}>{f.payment_method||"—"} · TXN: {f.transaction_id||"—"}</div>
                      <div style={{ fontSize:10,color:"#475569",marginTop:1 }}>{new Date(f.created_at*1000).toLocaleString("en-IN",{timeZone:"Asia/Kolkata"})}</div>
                    </div>
                    <span style={{ fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,background:f.status==="completed"?"rgba(74,222,128,.1)":f.status==="pending"?"rgba(251,191,36,.1)":"rgba(248,113,113,.1)",color:f.status==="completed"?"#4ade80":f.status==="pending"?"#fbbf24":"#f87171",border:`1px solid ${f.status==="completed"?"rgba(74,222,128,.25)":f.status==="pending"?"rgba(251,191,36,.25)":"rgba(248,113,113,.25)"}` }}>{f.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {listModal&&(
        <Modal onClose={()=>setListModal(false)}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16 }}>
            <div style={{ fontSize:15,fontWeight:700,color:"#f1f5f9" }}>{listModal==="save"?"💾 Save Name List":"📁 Saved Lists"}</div>
            <button onClick={()=>setListModal(false)} style={{ background:"none",border:"none",color:"#475569",fontSize:20,cursor:"pointer" }}>✕</button>
          </div>
          {listModal==="save"?(
            <><input style={{ ...inp,marginBottom:10 }} placeholder="List name…" value={listName} onChange={e=>setListName(e.target.value)}/><button onClick={saveList} style={{ width:"100%",background:"linear-gradient(135deg,#38bdf8,#818cf8)",color:"#fff",border:"none",borderRadius:9,padding:"11px",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>Save</button></>
          ):(
            Object.keys(savedLists).length===0?<Empty>No saved lists</Empty>
            :Object.keys(savedLists).map(k=>(
              <div key={k} style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:"1px solid rgba(255,255,255,.05)" }}>
                <span style={{ fontSize:13,color:"#94a3b8",fontWeight:500 }}>{k}</span>
                <div style={{ display:"flex",gap:7 }}>
                  <button onClick={()=>{F("custom_names",savedLists[k]);setListModal(false);}} style={{ background:"rgba(56,189,248,.09)",border:"1px solid rgba(56,189,248,.22)",color:"#38bdf8",borderRadius:6,padding:"4px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit" }}>Load</button>
                  <button onClick={()=>{const u={...savedLists};delete u[k];setSavedLists(u);localStorage.setItem("savedNameLists",JSON.stringify(u));}} style={{ background:"rgba(248,113,113,.07)",border:"1px solid rgba(248,113,113,.2)",color:"#f87171",borderRadius:6,padding:"4px 12px",fontSize:11,cursor:"pointer",fontFamily:"inherit" }}>Del</button>
                </div>
              </div>
            ))
          )}
        </Modal>
      )}
      <ConfirmModal
        open={!!confirmForceClient}
        title="Force Exit All Bots?"
        message="This will immediately disconnect all bots from the meeting. Are you sure?"
        confirmLabel="Force Exit"
        onConfirm={() => doForceConfirmed(confirmForceClient)}
        onCancel={() => setConfirmForceClient(null)}
      />
    </>
  );
}

function CampRow({ c, onStop, onForce, reactionsEnabled, stopReason }) {
  const joined=c.active||0,total=c.total_bots||1,pct=Math.min(100,Math.round((joined/total)*100));
  const isRun=c.status==="running"||c.status==="joining"||c.status==="provisioning";
  const hasReact=c.floating_emoji||c.participant_reactions;
  const startTime=c.started_at?fmtTime(c.started_at):c.schedule_at?"⏰ "+fmtTime(c.schedule_at):"—";
  return (
    <tr className="hov-row" style={{ borderBottom:"1px solid rgba(255,255,255,.045)",background:isRun?"rgba(56,189,248,0.03)":"transparent" }}>
      <td style={TDstyle}><span style={{ fontFamily:"'JetBrains Mono',monospace",fontSize:12,fontWeight:700,color:isRun?"#38bdf8":"#63b3ed",textShadow:isRun?"0 0 10px rgba(56,189,248,0.7)":"none" }}>{c.name||"—"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#cbd5e1",fontFamily:"'JetBrains Mono',monospace" }}>{c.zoom_url?.slice(0,26)}{c.zoom_url?.length>26?"…":""}</span></td>
      <td style={TDstyle}><span style={{ fontSize:13,color:"#e2e8f0",fontWeight:700 }}>{total}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,color:"#e2e8f0",fontWeight:700 }}>{startTime}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,color:"#94a3b8" }}>{c.leave_after_mins>0?c.leave_after_mins+" min":"∞ forever"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,color:"#94a3b8" }}>{c.name_type==="indian"?"🇮🇳 Hindi":c.name_type==="foreign"?"🌍 English":"✏️ Custom"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,color:"#94a3b8" }}>{c.meeting_type==="webinar"?"🎙 Webinar":c.meeting_type==="profile_pic"?"👤 Profile":"👥 Normal"}</span></td>
      <td style={TDstyle}>
        {reactionsEnabled
          ?<span style={{ fontSize:11,fontWeight:700,color:hasReact?"#4ade80":"#f87171",background:hasReact?"rgba(74,222,128,.1)":"rgba(248,113,113,.08)",border:`1px solid ${hasReact?"rgba(74,222,128,.25)":"rgba(248,113,113,.2)"}`,borderRadius:20,padding:"3px 10px",whiteSpace:"nowrap" }}>{hasReact?"✓ Yes":"✗ No"}</span>
          :<span style={{ fontSize:11,color:"#334155" }}>—</span>
        }
      </td>
      <td style={TDstyle}>
        <StatusDot status={c.status}/>
        {!isRun && stopReason && <div style={{ fontSize:9,color:"#f87171",marginTop:4,whiteSpace:"nowrap" }}>⚠ {fmtReason(stopReason)}</div>}
      </td>
    </tr>
  );
}

function HistoryRow({ h, pricePerMember, reactionPricePerMember, currency, reactionsEnabled }) {
  const hasReaction = !!(h.floating_emoji || h.participant_reactions);
  const effectivePrice = pricePerMember + (hasReaction ? (reactionPricePerMember||0) : 0);
  const hours = (h.leave_after_mins||120)/60;
  const mul = Math.round(hours)<=2?1:Math.round(hours)===3?2:Math.round(hours)===4?3:4;
  const cost = effectivePrice > 0
    ? (h.bots_sent||0) * effectivePrice * mul
    : (h.bots_sent||0) * (hours<=2?2:hours<=3?4:hours<=4?6:8) * mul;
  const hasReact=h.floating_emoji||h.participant_reactions;
  return (
    <tr className="hov-row" style={{ borderBottom:"1px solid rgba(255,255,255,.07)" }}>
      <td style={TDstyle}><span style={{ fontSize:12,fontWeight:700,color:"#e2e8f0" }}>{h.campaign_name||"—"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#cbd5e1",fontFamily:"'JetBrains Mono',monospace" }}>{h.zoom_url?.slice(0,26)}{h.zoom_url?.length>26?"…":""}</span></td>
      <td style={TDstyle}>
        <div style={{ fontSize:13,color:"#f1f5f9",fontWeight:800 }}>{h.bots_sent}<span style={{color:"#64748b",fontWeight:400}}>/{h.total_bots||"—"}</span></div>
        {h.bots_failed>0&&<div style={{fontSize:10,color:"#f87171",marginTop:2}}>✗ {h.bots_failed} failed</div>}
      </td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#e2e8f0" }}>{fmtTime(h.started_at)}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#e2e8f0" }}>{h.leave_after_mins>0?h.leave_after_mins+" min":"∞ forever"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#e2e8f0" }}>{h.name_type==="indian"?"🇮🇳 Hindi":h.name_type==="foreign"?"🌍 English":"✏️ Custom"}</span></td>
      <td style={TDstyle}><span style={{ fontSize:11,fontWeight:600,color:"#e2e8f0" }}>{h.meeting_type==="webinar"?"🎙 Webinar":h.meeting_type==="profile_pic"?"👤 Profile":"👥 Normal"}</span></td>
      <td style={TDstyle}>
        {reactionsEnabled
          ?<span style={{ fontSize:11,fontWeight:700,color:hasReact?"#4ade80":"#f87171",background:hasReact?"rgba(74,222,128,.1)":"rgba(248,113,113,.08)",border:`1px solid ${hasReact?"rgba(74,222,128,.25)":"rgba(248,113,113,.2)"}`,borderRadius:20,padding:"3px 10px",whiteSpace:"nowrap" }}>{hasReact?"✓ Yes":"✗ No"}</span>
          :<span style={{ fontSize:11,color:"#334155" }}>—</span>
        }
      </td>
      <td style={TDstyle}><StatusDot status={h.status}/></td>
      <td style={TDstyle}>
        <span style={{ fontSize:13,fontWeight:800,color:"#f87171" }}>−{fmtBal(cost,currency)}</span>
        <div style={{ fontSize:10,color:"#4a6280",marginTop:2 }}>{h.bots_sent||0} × {h.leave_after_mins||60}min</div>
      </td>
    </tr>
  );
}

const TDstyle = { padding:"11px 14px",verticalAlign:"middle" };

function AB({ c, onClick, children }) {
  return <button className="abtn" onClick={onClick} style={{ background:c+"14",color:c,border:`1px solid ${c}32`,borderRadius:7,padding:"5px 14px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit" }}>{children}</button>;
}
function Smb({ onClick, children }) {
  return <button className="smb" onClick={onClick} style={{ background:"rgba(255,255,255,.04)",border:"1px solid rgba(255,255,255,.1)",color:"#64748b",borderRadius:6,padding:"4px 11px",fontSize:11,cursor:"pointer",fontFamily:"inherit",transition:"all .15s" }}>{children}</button>;
}
function Empty({ children }) {
  return <div style={{ padding:"34px 22px",textAlign:"center",color:"#64748b",fontSize:12,letterSpacing:.3 }}>{children}</div>;
}
function Modal({ onClose, children }) {
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.88)",backdropFilter:"blur(12px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:500,padding:"0 16px" }}>
      <div style={{ background:"rgba(8,13,32,0.92)",backdropFilter:"blur(28px)",border:"1px solid rgba(255,255,255,0.09)",borderRadius:16,boxShadow:"0 8px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)",padding:26,width:"100%",maxWidth:400,animation:"floatUp .18s ease" }}>{children}</div>
    </div>
  );
}
